import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  combineLatest,
  EMPTY,
  iif,
  merge,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  BuyerSurveyResponse,
  DashboardChannel,
  PatchSurveyInterface,
  SupplierChannel,
  SupplierResponseInterface,
  SupplierSurveyStartChannel,
} from '../../shared/interfaces/survey.interface';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SurveyStatus } from '../../shared/utils/survey-status';
import { Constants } from '../constants/manage-multi-country.constants';
import { Router } from '@angular/router';
import {
  STATUS_BUTTONS,
  MCQB_ALLOWED_STATUSES,
} from '../../shared/constants/status-buttons';
import { INVOICE_TYPE } from '../../shared/constants/invoice-type';
import { CommonService } from '../../shared/services/common-service/common-service';
import { Constants as SURVEY_TABLE_CONSTANTS } from './../../shared/constants/surveys-table.constants';
import { TableExpandableRowDirective } from '@purespectrum1/ui/table';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { isEqual } from '../../create-surveys/utils/deep-compare';
import { SharedDataService } from '../../shared/services/shared-data-service/shared-data-service';
import { SocketService } from '../../shared/services/socket/socket.service';
import { BuyerProgressChannelV3 } from '../../shared/interfaces/socket.interface';
import { McSvpSurveyPayloadComposer } from '../../create-surveys/shared/ui/multicountry/domain/mc-svp-survey-payload-composer';
import { AuthService } from '../../shared/services/auth/auth.service';
import { MultiCountryService } from '../../create-surveys/shared/ui/multicountry/services/multi-country.service';
import { QuotaModalService } from '../../create-surveys/quota-modal/quota-modal.service';
import { notifyMessage } from '../../shared/constants/notify-message';
import { surveyPauseThresholdUpdateInspector } from '../../shared/domain/survey-pause-threshold-update-inspector';
import { SurveyHighCpiConfirmationInspector } from '../../shared/domain/survey-high-cpi-confirmation-inspector';
import { ModalService } from '@purespectrum1/ui/modal';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { LayoutConstants } from '../../create-surveys/layout/layout-constants';
import {
  MultiCountryTableData,
  SupplierDataResponse,
} from './manage-multi-country.interface';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'ps-ui-manage-multi-country',
  templateUrl: './manage-multi-country.component.html',
  styleUrls: ['./manage-multi-country.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMultiCountryComponent implements OnInit, OnDestroy {
  @ViewChild(TableExpandableRowDirective)
  public expandable!: TableExpandableRowDirective;

  public onExpandRow(row: number): void {
    this.expandable.expand(row);
  }

  @Input() mcUniqueId?: string = '';
  @Input() surveyId!: number | undefined;

  private _subscription = new Subscription();
  public isSingleCountrySurvey: boolean = false;
  public constants = Constants;
  public isSvpSurvey: boolean = false;
  public surveys$: Observable<MultiCountryTableData[]>;
  public MAX_CPI_THRESHOLD = LayoutConstants.MAX_CPI_THRESHOLD;
  public transformedSurveys: MultiCountryTableData[] = [];

  private _updateSurvey$ = new EventEmitter<{
    action: 'cpi' | 'completes';
    survey: MultiCountryTableData;
  }>();
  private _getSuppliers$ = new EventEmitter<{
    surveyId: number;
    row: number;
  }>();
  public suppliers$: Observable<SupplierDataResponse[]>;
  private _selectedSurveyId: number;
  private _selectedSupplierList: SupplierResponseInterface[] = [];
  private _surveyList: BuyerSurveyResponse[] = [];
  private _mcSvpPayloadComposer = new McSvpSurveyPayloadComposer(
    this._auth.userType
  );
  private _surveyIds: number[] = [];

  constructor(
    private _buyerApiService: BuyerApiService,
    private _commonService: CommonService,
    private _shareDataService: SharedDataService,
    private _route: Router,
    private _auth: AuthService,
    private _toaster: ToasterService,
    private _socketService: SocketService,
    private _multiCountryService: MultiCountryService,
    private _quotaModalService: QuotaModalService,
    private _modal: ModalService,
    private _cdr: ChangeDetectorRef
  ) {}

  surveyStatus = SurveyStatus;
  public statusButtons = STATUS_BUTTONS;

  public ngOnInit(): void {
    this._mcSvpPayloadComposer = new McSvpSurveyPayloadComposer(
      this._auth.userType,
      this._auth.isLoggedInAsServiceBuyer()
    );
    this.isSingleCountrySurvey = !this.mcUniqueId;

    let status = Object.values(SurveyStatus).filter(
      (status) => typeof status === 'number'
    );

    const update$ = this._updateSurvey$.pipe(
      switchMap((data) => {
        if (data.action !== 'cpi') {
          return of(data);
        }
        return this._checkAndOpenHighCpiConfirmationModal(
          data.survey.cpi_current,
          data.survey
        ).pipe(map(() => data));
      }),
      map((data) => {
        if (data.action === 'cpi') {
          data.survey.is_cpi_editable = false;
        }
        return data;
      }),
      switchMap(({ survey, action }) => {
        if (this.isSvpSurvey) {
          const payload = this.getPutPayload(action, survey);
          return this._multiCountryService
            .updateSurvey(payload, this._mcSvpPayloadComposer.surveyId())
            .pipe(
              map(() => ({ survey, action })),
              catchError((err) => this._handleError(err))
            );
        } else {
          const payload = this.getPatchPayload(action, survey);
          return this._buyerApiService
            .patchSurvey(survey.ps_survey_id!, payload)
            .pipe(
              map(() => ({ survey, action })),
              catchError((err) => this._handleError(err))
            );
        }
      }),
      tap(({ action }) => {
        if (action === 'cpi') {
          this._toaster.success(notifyMessage.successMessage.CPI_APPLIED);
        }
      }),
      map(({ survey }) => survey as Partial<MultiCountryTableData>)
    );

    const getSuppliers$ = this._getSuppliers$.pipe(
      distinctUntilChanged((prev, curr) => {
        if (isEqual(prev, curr)) {
          this.expandable.expand(curr.row);
          return true;
        }
        return false;
      }),
      switchMap(({ surveyId, row }) =>
        this._buyerApiService
          .getSuppliers(
            surveyId,
            'view',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            false // supplier summary
          )
          .pipe(
            map((suppliers) => (this._selectedSupplierList = suppliers)),
            catchError((error) => {
              this._toaster.error(error.message);
              return of([]);
            }),
            tap(() => this.onExpandRow(row))
          )
      )
    );

    const supplierSurveyStartChannelV3$ =
      this._socketService.supplierSurveyStartChannelV3$.pipe(
        map((data: SupplierSurveyStartChannel) =>
          this._updateSurveyStartViaSocket(data)
        ),
        filter((suppliers) => !!suppliers.length)
      );

    const supplierChannel$ = this._socketService.supplierChannel$.pipe(
      map((data: SupplierChannel) =>
        this._updateSupplierChannelViaSocket(data)
      ),
      filter((suppliers) => !!suppliers.length)
    );

    this.suppliers$ = merge(
      supplierSurveyStartChannelV3$,
      supplierChannel$,
      getSuppliers$
    ).pipe(map((suppliers) => this._transformSuppliers(suppliers)));

    const dashboardChannel$ = this._socketService.dashboardChannel$.pipe(
      map((data: DashboardChannel) =>
        this._updateDashboardChannelViaSocket(data)
      )
    );

    const buyerProgressChannelV3$ =
      this._socketService.buyerProgressChannelV3$.pipe(
        map((data: BuyerProgressChannelV3) =>
          this._updateBuyerProgressChannelV3ViaSocket(data)
        )
      );

    const getSurvey$ = of(status).pipe(
      map((status) => status.join(',')),
      switchMap((status) =>
        this._buyerApiService.getSurveys(status, 1, '', this.mcUniqueId, 1)
      ),
      map((surveys) => {
        this._mcSvpPayloadComposer.initial(surveys);
        return surveys;
      }),
      map((surveys) => this._filterCurrentSurvey(surveys)),
      map((surveys) => (this._surveyList = surveys)),
      map((surveys) => this._tranformSurveys(surveys)),
      tap((surveys) => {
        this._surveyIds = this._getSurveyIdsFromList(surveys);
        // Subscribe to relevant surveys
        this._socketService.subscribeToSurveys(this._surveyIds);
        this.transformedSurveys = surveys;
      })
    );

    const surveys$ = getSurvey$.pipe(
      tap(([survey]) => (this.isSvpSurvey = !!survey.is_svp)),
      tap(
        () =>
          this.isSvpSurvey &&
          (this.statusButtons = this.statusButtons.filter((statusButton) =>
            MCQB_ALLOWED_STATUSES.includes(statusButton.value)
          ))
      )
    );

    const updatedSurveys$ = combineLatest([
      surveys$,
      merge(update$, dashboardChannel$, buyerProgressChannelV3$),
    ]).pipe(
      map(([surveys, update]) => {
        const index = surveys.findIndex(
          (s) => s.ps_survey_id === update.ps_survey_id
        );

        surveys[index] = {
          ...surveys[index],
          ...update,
        };
        return surveys;
      })
    );

    this.surveys$ = iif(
      () => !!this.surveyId && !!this.mcUniqueId,
      merge(surveys$, updatedSurveys$),
      of([])
    ).pipe(startWith([]));

    this._subscription.add(
      this.surveys$.subscribe((surveys) => {
        this.transformedSurveys = [...surveys];
        this._cdr.detectChanges();
      })
    );
  }

  private _handleError(err: HttpErrorResponse) {
    this._toaster.error(err?.error?.ps_api_response_message ?? 'Server Error');
    return EMPTY;
  }
  private _getSurveyIdsFromList(surveys: MultiCountryTableData[]): number[] {
    return surveys
      .map((s) => s.ps_survey_id)
      .filter((id): id is number => id !== undefined);
  }

  private _updateSupplierChannelViaSocket(data: SupplierChannel) {
    if (
      this._selectedSurveyId == data.surveyId &&
      this._selectedSupplierList.length
    ) {
      this._selectedSupplierList = this._selectedSupplierList.map((sup) =>
        sup.suppliers[0].id === data.supplierId
          ? { ...sup, ...data.supplier }
          : sup
      );
      return this._selectedSupplierList;
    }
    return [];
  }

  private _updateSurveyStartViaSocket(data: SupplierSurveyStartChannel) {
    if (
      this._selectedSurveyId == data.surveyId &&
      this._selectedSupplierList.length
    ) {
      this._selectedSupplierList = this._selectedSupplierList.map((sup) =>
        sup.suppliers[0].id === data.supplierId
          ? {
              ...sup,
              srv_start_count: data.startSurveyCount,
              last_start_date: String(data.lastSurveyStartDate),
            }
          : sup
      );
      return this._selectedSupplierList;
    }
    return [];
  }

  private _updateBuyerProgressChannelV3ViaSocket(
    data: BuyerProgressChannelV3
  ): Partial<MultiCountryTableData> {
    const index = this.transformedSurveys.findIndex(
      (survey) => survey.ps_survey_id === data.surveyId
    );

    if (index !== -1 && 'actualBuyerInProgress' in data) {
      this.transformedSurveys[index].in_progress = data.actualBuyerInProgress;
      return this.transformedSurveys[index];
    }
    return {};
  }

  private _updateDashboardChannelViaSocket(
    data: DashboardChannel
  ): Partial<MultiCountryTableData> {
    const { surveyManagement: { survey_id: socketSurveyId, fielded } = {} } =
      data;

    const index = this.transformedSurveys.findIndex(
      (survey) => survey.ps_survey_id === socketSurveyId
    );

    if (index !== -1 && fielded) {
      this.transformedSurveys[index].fielded = fielded;
      return this.transformedSurveys[index];
    }
    return {};
  }

  private _filterCurrentSurvey(surveys: BuyerSurveyResponse[]) {
    return !!surveys[0]?.mc?.SVP
      ? surveys.filter((survey) => !survey?.mc?.parent)
      : surveys.filter((survey) => survey.ps_survey_id !== this.surveyId);
  }

  private _tranformSurveys(
    surveys: BuyerSurveyResponse[]
  ): MultiCountryTableData[] {
    return surveys.map((mSurvey): MultiCountryTableData => {
      return {
        ps_survey_status: mSurvey.ps_survey_status,
        ps_survey_id: mSurvey.ps_survey_id,
        survey_title: mSurvey.survey_title,
        survey_type: 'Multicountry',
        country_name: mSurvey.locale.country_name,
        language_name: mSurvey.locale.language_translate.toUpperCase(),
        creation_date: mSurvey.project_creation_date,
        fielded: mSurvey.fielded,
        goal: mSurvey.completes_required,
        currencySymbol: mSurvey.currency_symbol,
        avg_cpi: mSurvey.average_cpi ? mSurvey.average_cpi : 0,
        cpi_current: mSurvey.acpi ? mSurvey.acpi : 0,
        epc: mSurvey.epc,
        current_ir: mSurvey.expected_ir,
        total_starts: mSurvey.total_starts,
        last_24_starts: mSurvey.last_24_starts,
        in_progress: mSurvey.bip ? mSurvey.bip : 0,
        create_status: this.constants.CHILD_CREATION_STATUS.READY,
        invoiceType: mSurvey.company.generate_invoice,
        is_svp: !!mSurvey.mc.SVP,
        completes_required: mSurvey.completes_required,
        survey_localization: mSurvey.survey_localization,
        qualifications: mSurvey.qualifications,
        quotas: mSurvey.quotas,
        click_balancing: mSurvey.click_balancing,
        expected_ir: mSurvey.expected_ir,
        is_enable_margin_override: !!mSurvey.is_enable_margin_override,
        is_survey_pause_threshold_triggered:
          mSurvey.is_survey_pause_threshold_triggered,
      };
    });
  }

  private _transformSuppliers(
    suppliers: SupplierResponseInterface[]
  ): SupplierDataResponse[] {
    return suppliers
      .filter((supplier) => {
        return supplier.completes_goal || supplier.srv_start_count;
      })
      .map((supplier) => {
        return {
          ...supplier,
          name: supplier.group_ref
            ? supplier.group_name
            : supplier.suppliers[0].name,
          fielded_vs_goal: `${
            supplier.click_balance ? supplier.valid_clicks : supplier.fielded
          } | ${supplier.completes_goal}`, // should confirm with claudio, current target or completes ?
          supplier_currently_open: supplier.completes_goal - supplier.fielded,
          last_complete_date: supplier?.last_complete_date,
          last_start_date: supplier?.last_start_date,
        };
      });
  }

  getHeaders() {
    const headers = Object.values(this.constants.MC_SURVEY_TABLE_HEADERS);
    const excludedHeader =
      this._auth.userType === 'buyer'
        ? [
            this.constants.MC_SURVEY_TABLE_HEADERS.SURVEY_ID.label,
            this.constants.MC_SURVEY_TABLE_HEADERS.CURRENT_IR.label,
          ]
        : [this.constants.MC_SURVEY_TABLE_HEADERS.CURRENT_IR.label];
    return !this.isSvpSurvey
      ? headers
      : headers.filter((header) => !excludedHeader.includes(header.label));
  }

  redirectToSurvey(survey: MultiCountryTableData) {
    if (!survey.is_svp) {
      this._route.navigateByUrl(`/dashboard/${survey.ps_survey_id}`);
    }
  }

  public progress(survey: MultiCountryTableData) {
    return ((survey?.fielded ?? 0) * 100) / survey.completes_required!;
  }

  public isSurveyStatusDropdownDisabled(survey: MultiCountryTableData) {
    return (
      (INVOICE_TYPE.MONTHLY === survey?.invoiceType &&
        SurveyStatus.Closed === survey.ps_survey_status) ||
      [SurveyStatus.Invoice, SurveyStatus.Draft].indexOf(
        survey.ps_survey_status!
      ) > -1 ||
      (this.isSvpSurvey &&
        [SurveyStatus.Closed, SurveyStatus.Invoice].includes(
          survey.ps_survey_status!
        )) ||
      (this.isSvpSurvey &&
        this._mcSvpPayloadComposer.surveyStatus() === SurveyStatus.Paused)
    );
  }

  openStatusModal(survey: MultiCountryTableData, status: string) {
    const pauseThresholdUpdateInfo = this._getPauseThresholdUpdateInfoText(
      survey,
      status
    );
    this._shareDataService
      .openStatusModal(
        survey.ps_survey_id!,
        status,
        `${survey.country_name}-${survey.language_name}`,
        !!survey.is_svp,
        pauseThresholdUpdateInfo
      )
      .subscribe((msg) => {
        if (msg === SURVEY_TABLE_CONSTANTS.STATUS_CHANGE_MESSAGE) {
          const surveyPsStatus = Number(
            this._commonService.getSurveyStatus(status)
          );
          survey.ps_survey_status = surveyPsStatus;

          this._commonService.onChangeSurveyStatus({
            surveyId: survey.ps_survey_id!,
            status: surveyPsStatus,
          });
        }
      });
  }

  private _getPauseThresholdUpdateInfoText(
    survey: MultiCountryTableData,
    status: string
  ): string {
    const nextStatus = this._commonService.getSurveyStatus(status);
    const currentStatus = survey.ps_survey_status!;
    const data = {
      is_survey_pause_threshold_triggered:
        survey.is_survey_pause_threshold_triggered || false,
      current_cpi: survey.cpi_current,
      currency_symbol: survey.currencySymbol,
    };
    const inspector = new surveyPauseThresholdUpdateInspector(
      currentStatus,
      nextStatus,
      data
    );
    return inspector.text();
  }

  getPutPayload(action: string, survey: MultiCountryTableData) {
    switch (action) {
      case 'cpi':
        survey.cpi_current = Number(survey.cpi_current.toFixed(2));
        this._mcSvpPayloadComposer.set({
          for: 'each',
          data: [
            {
              survey_localization: survey.survey_localization,
              offer_price: survey.cpi_current,
            },
          ],
        });
        break;
      case 'completes':
        this._mcSvpPayloadComposer.set({
          for: 'each',
          data: [
            {
              survey_localization: survey.survey_localization,
              completes_required: survey.completes_required,
              quotas: this.distributeQuotaPayloadOnCompleteChange(
                survey as unknown as BuyerSurveyResponse
              ),
            },
          ],
        });
        break;
    }
    return this._mcSvpPayloadComposer.payload()!;
  }

  distributeQuotaPayloadOnCompleteChange(survey: BuyerSurveyResponse) {
    if (survey.quotas?.length) {
      const childSurvey =
        this._quotaModalService.redistributeQuotasForSpvSurvey(
          survey,
          !!survey.click_balancing
        );
      return childSurvey.quotas;
    }
    return [];
  }

  getPatchPayload(action: string, survey: MultiCountryTableData) {
    const payload: PatchSurveyInterface[] = [];

    switch (action) {
      case 'cpi':
        survey.cpi_current = Number(survey.cpi_current.toFixed(2));
        payload.push({
          op: 'replace',
          path: '/offer_price',
          value: survey.cpi_current,
        });
        break;
      case 'completes':
        payload.push({
          op: 'replace',
          path: '/completes_required',
          value: survey.completes_required!,
        });
        break;
    }
    return payload;
  }

  public updateSurvey(
    survey: MultiCountryTableData,
    action: 'cpi' | 'completes'
  ) {
    const {
      completes_required = 0,
      changed_completes = 0,
      cpi_current,
      initial_cpi,
      fielded = 0,
    } = survey;
    if (
      action === 'completes' &&
      (!completes_required || completes_required < fielded)
    ) {
      survey.completes_required = changed_completes;
      return;
    }

    if (action === 'cpi') {
      if (!this._validateCpi(survey)) {
        survey.cpi_current = initial_cpi ?? cpi_current;
        return;
      }
    }
    this._updateSurvey$.next({ survey, action });
  }

  private _validateCpi(survey: MultiCountryTableData) {
    const { cpi_current } = survey;
    if (!cpi_current) {
      this._toaster.error(notifyMessage.errorMessage.EDIT_CPI.EMPTY_VALUE);
      return false;
    }

    return true;
  }

  private _checkAndOpenHighCpiConfirmationModal(
    cpi: number,
    survey: MultiCountryTableData
  ): Observable<boolean> {
    const inspector = new SurveyHighCpiConfirmationInspector({
      data_type: 'survey-data',
      cpi,
      surveyData: {
        current_cpi: survey.cpi_current,
        currency_symbol: survey.currencySymbol,
      },
    });
    if (!inspector.check()) {
      return of(true);
    }

    return this._modal
      .open(ConfirmationModalComponent, {
        data: {
          innerHtml: inspector.text(),
          confirmButtonLabel: 'Confirm',
          cpi_current: cpi,
          ps_survey_id: survey.ps_survey_id,
        },
        maxWidth: '25%',
      })
      .onClose$.pipe(filter((val) => val === 'ok'));
  }

  ngOnDestroy(): void {
    // unsubscribe already subscribed survey list of multi country surveys
    this._socketService.unsubscribeFromSurveys(this._surveyIds);
    if (this._selectedSurveyId) {
      // unsubscribe already subscribed survey
      this._socketService.leaveSupplierTabRoom(this._selectedSurveyId);
    }
    this._subscription.unsubscribe();
  }

  getSuppliersById(surveyId: number, row: number) {
    if (this._selectedSurveyId) {
      // unsubscribe already subscribed survey
      this._socketService.leaveSupplierTabRoom(this._selectedSurveyId);
    }
    // subscribe new survey which opens in accordion
    this._socketService.joinSupplierTabRoom(surveyId);
    this._selectedSurveyId = surveyId;
    this._getSuppliers$.next({ surveyId, row });
  }
}
